import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
	getAllParentsOptions,
	getAllCoursesOptions,
	getAllTeachersOptions,
} from "@/lib/client/@tanstack/react-query.gen";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Users, BookOpen, Euro, UserCog } from "lucide-react";
import { useNavigate } from "@tanstack/react-router";

export const Route = createFileRoute("/admin/")({
	component: AdminDashboard,
});

function AdminDashboard() {
	const navigate = useNavigate();

	const { data: parents, isLoading: parentsLoading } = useQuery(
		getAllParentsOptions(),
	);
	const { data: courses, isLoading: coursesLoading } = useQuery(
		getAllCoursesOptions(),
	);
	const { data: teachers, isLoading: teachersLoading } = useQuery(
		getAllTeachersOptions(),
	);

	if (parentsLoading || coursesLoading || teachersLoading) {
		return (
			<div className="flex items-center justify-center min-h-[50vh]">
				<div className="text-center px-4">
					<div className="animate-spin rounded-full h-10 w-10 lg:h-12 lg:w-12 border-b-2 border-gray-900 mx-auto"></div>
					<p className="mt-4 text-sm lg:text-base text-gray-600">Chargement du dashboard...</p>
				</div>
			</div>
		);
	}

	const totalParents = parents?.length || 0;
	const totalCourses = courses?.length || 0;
	const totalTeachers = teachers?.length || 0;

	return (
		<div className="space-y-4 lg:space-y-6">
			<div>
				<h1 className="text-2xl lg:text-3xl font-bold">Dashboard Administration</h1>
				<p className="text-sm lg:text-base text-gray-600 mt-1 lg:mt-2">
					Vue d'ensemble de l'activité Revacours
				</p>
			</div>

			<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
				<Card>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 p-4 lg:p-6">
						<CardTitle className="text-sm font-medium">
							Parents inscrits
						</CardTitle>
						<Users className="h-4 w-4 text-muted-foreground" />
					</CardHeader>
					<CardContent className="p-4 pt-0 lg:p-6 lg:pt-0">
						<div className="text-2xl font-bold">{totalParents}</div>
						<Button
							variant="link"
							className="p-0 h-auto text-sm"
							onClick={() => navigate({ to: "/admin/parents" })}
						>
							Gérer les parents →
						</Button>
					</CardContent>
				</Card>

				<Card>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 p-4 lg:p-6">
						<CardTitle className="text-sm font-medium">
							Cours disponibles
						</CardTitle>
						<BookOpen className="h-4 w-4 text-muted-foreground" />
					</CardHeader>
					<CardContent className="p-4 pt-0 lg:p-6 lg:pt-0">
						<div className="text-2xl font-bold">{totalCourses}</div>
						<Button
							variant="link"
							className="p-0 h-auto text-sm"
							onClick={() => navigate({ to: "/admin/courses" })}
						>
							Gérer les cours →
						</Button>
					</CardContent>
				</Card>

				<Card>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 p-4 lg:p-6">
						<CardTitle className="text-sm font-medium">
							Professeurs
						</CardTitle>
						<UserCog className="h-4 w-4 text-muted-foreground" />
					</CardHeader>
					<CardContent className="p-4 pt-0 lg:p-6 lg:pt-0">
						<div className="text-2xl font-bold">{totalTeachers}</div>
						<Button
							variant="link"
							className="p-0 h-auto text-sm"
							onClick={() => navigate({ to: "/admin/teachers" })}
						>
							Gérer les professeurs →
						</Button>
					</CardContent>
				</Card>
			</div>

			<div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
				<Card>
					<CardHeader className="p-4 lg:p-6">
						<CardTitle className="text-base lg:text-lg">Actions rapides</CardTitle>
						<CardDescription className="text-sm">
							Accès rapide aux fonctionnalités principales
						</CardDescription>
					</CardHeader>
					<CardContent className="p-4 pt-0 lg:p-6 lg:pt-0">
						<div className="space-y-2 lg:space-y-3">
							<Button
								className="w-full justify-start text-sm lg:text-base"
								variant="outline"
								onClick={() => navigate({ to: "/admin/attendance" })}
							>
								<Users className="mr-2 h-4 w-4" />
								Enregistrer les présences
							</Button>
							<Button
								className="w-full justify-start text-sm lg:text-base"
								variant="outline"
								onClick={() => navigate({ to: "/admin/payments" })}
							>
								<Euro className="mr-2 h-4 w-4" />
								Traiter les paiements
							</Button>
						</div>
					</CardContent>
				</Card>
			</div>
		</div>
	);
}
